import React, { useState, useMemo, useEffect, useRef, ReactNode } from 'react';
import { useSetRecoilState, useRecoilValue, useRecoilState } from 'recoil';
import {
  errorModalTextRecoil,
  completeModalRecoil,
  modifyModalRecoil,
  addressModalRecoil,
  placeAddModalRecoil,
} from '@/atom/ModalAtom';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import LS from '@/styles/layoutStyled';
import CS from '@/styles/componentStyled';
import BS from '@/styles/btnStyled';
import IS from '@/styles/inputStyled';
import { ApiService } from '@/api';
import ModalType1 from '@/modal/ModalType1';
import AddressModal from './AddressModal';
import { PlaceDto } from '@/model/place.dto';

const PlaceAddModal = () => {
  const accessToken = sessionStorage.getItem('accessToken');
  const api = useMemo(() => new ApiService(), []);

  const [name, setName] = useState<string>('');
  const [nameDuplication, setNameDuplication] = useState(false);
  const [phone, setPhone] = useState<string>('');
  const [manager, setManager] = useState<string>('');
  const [address, setAddress] = useState<string>('');
  const [detail, setDetail] = useState<string>('');

  const setErrorModalText = useSetRecoilState(errorModalTextRecoil);
  const setCompleteModalState = useSetRecoilState(completeModalRecoil);
  const setPlaceAddModalState = useSetRecoilState(placeAddModalRecoil);
  const [addressModalState, setAddressModalState] =
    useRecoilState(addressModalRecoil);

  const handleNameDuplication = async () => {
    if (name.trim() === '') {
      setErrorModalText('출입 장소를 입력해 주세요.');
      return;
    }
    try {
      await api.getPlaceCheck(String(accessToken), name);
      setNameDuplication(true);
    } catch (err: any) {
      if (err.response.data.message === 'Place Name Already Exist') {
        setErrorModalText('중복된 출입 장소입니다.');
      }
    }
  };

  const handleAddressChange = (value: string) => {
    setAddress(value);
    setDetail('');
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (
      name.trim() === '' ||
      manager.trim() === '' ||
      address.trim() === '' ||
      detail.trim() === ''
    ) {
      setErrorModalText('빈 칸을 확인해 주세요.');
      return;
    }

    if (phone.trim() !== '') {
      if (!Number(phone)) {
        setErrorModalText('연락처는 숫자만 입력할 수 있습니다.');
        return;
      }
    }

    if (!nameDuplication) {
      setErrorModalText('출입 장소 중복 확인을 해주세요.');
      return;
    }

    const dto: PlaceDto = {
      name,
      manager,
      phone: phone.trim(),
      address,
      detail,
      is_active: false,
    };

    try {
      await api.postPlaceAdd(String(accessToken), dto);
      setCompleteModalState(true);
    } catch (err: any) {
      console.error(err);
    }
  };

  return (
    <>
      <ModalType1>
        <div className="title-box flex aic jcsb">
          <h1>출입장소 등록</h1>
          <button
            className="x-btn img-box"
            onClick={() => setPlaceAddModalState(false)}
          >
            <img src="./img/ico_x_444.svg" alt="" />
          </button>
        </div>
        <div className="cnt-wrap">
          <h3>
            <span className="inblock">*</span>필수 항목
          </h3>
          <form onSubmit={handleSubmit}>
            <CS.TableType1_2 className="table-wrap">
              <table>
                <tbody>
                  <tr>
                    <th>
                      출입 장소<span className="star-point">*</span>
                    </th>
                    <td>
                      <div className="confirm-btn-box flex aic">
                        <IS.TextInputType1
                          type="text"
                          placeholder="출입 장소"
                          value={name}
                          onChange={(e) => {
                            setName(e.target.value);
                            setNameDuplication(false);
                          }}
                        />
                        <BS.Btn3Type3
                          type="button"
                          onClick={handleNameDuplication}
                          disabled={nameDuplication}
                        >
                          {nameDuplication ? '완료' : '중복 조회'}
                        </BS.Btn3Type3>
                      </div>
                    </td>
                    <th>연락처</th>
                    <td>
                      <IS.TextInputType1
                        type="text"
                        placeholder="연락처"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>
                      관리자<span className="star-point">*</span>
                    </th>
                    <td colSpan={3}>
                      <IS.TextInputType1
                        type="text"
                        placeholder="관리자"
                        value={manager}
                        onChange={(e) => setManager(e.target.value)}
                      />
                    </td>
                  </tr>
                  <tr>
                    <th>
                      주소<span className="star-point">*</span>
                    </th>
                    <td className="address" colSpan={3}>
                      <div className="confirm-btn-box flex aic">
                        <IS.TextInputType1
                          className="address-1"
                          type="text"
                          placeholder="기본주소"
                          readOnly={true}
                          value={address}
                          onChange={(e) => setAddress(e.target.value)}
                        />
                        <BS.Btn3Type3
                          type="button"
                          onClick={() => setAddressModalState(true)}
                        >
                          주소 검색
                        </BS.Btn3Type3>
                      </div>
                      <IS.TextInputType1
                        className="address-2"
                        type="text"
                        placeholder="상세주소"
                        value={detail}
                        onChange={(e) => setDetail(e.target.value)}
                      />
                    </td>
                  </tr>
                </tbody>
              </table>
            </CS.TableType1_2>
            <div className="btn-wrap">
              <BS.Btn2Type3
                type="button"
                onClick={() => setPlaceAddModalState(false)}
              >
                취소
              </BS.Btn2Type3>
              <BS.Btn2Type4 type="submit">등록</BS.Btn2Type4>
            </div>
          </form>
        </div>
      </ModalType1>
      {addressModalState && (
        <AddressModal onAddressChange={handleAddressChange} />
      )}
    </>
  );
};

export default PlaceAddModal;
